import { useState } from "react";
import { motion } from "framer-motion";
import { Send, MessageCircle } from "lucide-react";
import { useTranslation } from "react-i18next";

const BookingForm = () => {
  const { t, i18n } = useTranslation();
  const [form, setForm] = useState({
    name: "",
    phone: "",
    from: "",
    to: "",
    date: "",
    time: "",
    passengers: "",
    bags: "",
    payment: "",
    details: "",
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    // Choose labels based on language for the WhatsApp message
    const labels = i18n.language === "ar" ? {
      header: "🚗 *طلب حجز جديد*",
      name: "👤 الاسم",
      phone: "📱 الهاتف",
      from: "📍 من",
      to: "📍 إلى",
      date: "📅 التاريخ",
      time: "🕐 الوقت",
      passengers: "👥 عدد الأفراد",
      bags: "🧳 عدد الشنط",
      payment: "💳 طريقة الدفع",
      details: "📝 تفاصيل",
      none: "لا يوجد"
    } : {
      header: "🚗 *New Booking Request*",
      name: "👤 Name",
      phone: "📱 Phone",
      from: "📍 From",
      to: "📍 To",
      date: "📅 Date",
      time: "🕐 Time",
      passengers: "👥 Passengers",
      bags: "🧳 Bags",
      payment: "💳 Payment",
      details: "📝 Details",
      none: "None"
    };

    const message = `${labels.header}
━━━━━━━━━━━━
${labels.name}: ${form.name}
${labels.phone}: ${form.phone}
${labels.from}: ${form.from}
${labels.to}: ${form.to}
${labels.date}: ${form.date}
${labels.time}: ${form.time}
${labels.passengers}: ${form.passengers}
${labels.bags}: ${form.bags}
${labels.payment}: ${form.payment}
${labels.details}: ${form.details || labels.none}`;

    const encoded = encodeURIComponent(message);
    window.open(`https://wa.me/201029009588?text=${encoded}`, "_blank");
  };

  const inputClasses =
    "w-full bg-secondary/50 border border-border rounded-xl px-4 py-3.5 text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-primary/50 focus:ring-1 focus:ring-primary/30 transition-all";

  return (
    <section id="booking" className="py-24 section-gradient">
      <div className="container mx-auto px-6 max-w-3xl">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <span className="text-primary text-sm font-semibold tracking-wider">{t("booking.badge")}</span>
          <h2 className="text-4xl md:text-5xl font-bold mt-3 text-foreground">
            {t("booking.title")} <span className="gold-text">{t("booking.now")}</span>
          </h2>
          <div className="w-20 h-1 gold-gradient mx-auto mt-6 rounded-full" />
        </motion.div>

        <motion.form
          onSubmit={handleSubmit}
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="glass-card rounded-3xl p-8 md:p-10 gold-border-glow"
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div>
              <label htmlFor="name" className="block text-sm text-foreground/70 mb-2">{t("booking.form.name")}</label>
              <input
                id="name"
                type="text"
                name="name"
                required
                maxLength={100}
                value={form.name}
                onChange={handleChange}
                className={inputClasses}
                placeholder={t("booking.form.namePlaceholder")}
              />
            </div>
            <div>
              <label htmlFor="phone" className="block text-sm text-foreground/70 mb-2">{t("booking.form.phone")}</label>
              <input
                id="phone"
                type="tel"
                name="phone"
                required
                maxLength={20}
                value={form.phone}
                onChange={handleChange}
                className={inputClasses}
                placeholder={t("booking.form.phonePlaceholder")}
              />
            </div>
            <div>
              <label htmlFor="from" className="block text-sm text-foreground/70 mb-2">{t("booking.form.from")}</label>
              <input
                id="from"
                type="text"
                name="from"
                required
                maxLength={100}
                value={form.from}
                onChange={handleChange}
                className={inputClasses}
                placeholder={t("booking.form.fromPlaceholder")}
              />
            </div>
            <div>
              <label htmlFor="to" className="block text-sm text-foreground/70 mb-2">{t("booking.form.to")}</label>
              <input
                id="to"
                type="text"
                name="to"
                required
                maxLength={100}
                value={form.to}
                onChange={handleChange}
                className={inputClasses}
                placeholder={t("booking.form.toPlaceholder")}
              />
            </div>
            <div>
              <label htmlFor="date" className="block text-sm text-foreground/70 mb-2">{t("booking.form.date")}</label>
              <input
                id="date"
                type="date"
                name="date"
                required
                value={form.date}
                onChange={handleChange}
                className={inputClasses}
              />
            </div>
            <div>
              <label htmlFor="time" className="block text-sm text-foreground/70 mb-2">{t("booking.form.time")}</label>
              <input
                id="time"
                type="time"
                name="time"
                required
                value={form.time}
                onChange={handleChange}
                className={inputClasses}
              />
            </div>
            <div>
              <label htmlFor="passengers" className="block text-sm text-foreground/70 mb-2">{t("booking.form.passengers")}</label>
              <input
                id="passengers"
                type="number"
                name="passengers"
                min="1"
                max="50"
                required
                value={form.passengers}
                onChange={handleChange}
                className={inputClasses}
                placeholder="1"
              />
            </div>
            <div>
              <label htmlFor="bags" className="block text-sm text-foreground/70 mb-2">{t("booking.form.bags")}</label>
              <input
                id="bags"
                type="number"
                name="bags"
                min="0"
                max="50"
                value={form.bags}
                onChange={handleChange}
                className={inputClasses}
                placeholder="0"
              />
            </div>
          </div>

          <div className="mt-5">
            <label htmlFor="payment" className="block text-sm text-foreground/70 mb-2">{t("booking.form.payment")}</label>
            <select
              id="payment"
              name="payment"
              required
              value={form.payment}
              onChange={handleChange}
              className={inputClasses}
            >
              <option value="">{t("booking.form.paymentPlaceholder")}</option>
              <option value={t("booking.form.cash")}>{t("booking.form.cash")}</option>
              <option value={t("booking.form.instapay")}>{t("booking.form.instapay")}</option>
            </select>
          </div>

          <div className="mt-5">
            <label htmlFor="details" className="block text-sm text-foreground/70 mb-2">{t("booking.form.details")}</label>
            <textarea
              id="details"
              name="details"
              maxLength={500}
              value={form.details}
              onChange={handleChange}
              className={`${inputClasses} resize-none h-28`}
              placeholder={t("booking.form.detailsPlaceholder")}
            />
          </div>

          <motion.button
            type="submit"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            className="w-full mt-8 gold-gradient py-4 rounded-2xl text-primary-foreground font-bold text-lg flex items-center justify-center gap-3 hover:opacity-90 transition-all"
          >
            <MessageCircle size={22} />
            {t("booking.form.submit")}
            <Send size={18} />
          </motion.button>
        </motion.form>
      </div>
    </section>
  );
};

export default BookingForm;
